import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Form, Button, Alert, Card, Collapse, Row, Col, Badge, Spinner } from 'react-bootstrap';
import {
  FaUser,
  FaIdCard,
  FaGraduationCap,
  FaHardHat,
  FaCalendarDay,
  FaClock,
  FaCheckCircle,
  FaSave,
  FaSearch,
  FaPrint,
  FaListUl,
  FaEye,
  FaEyeSlash,
  FaHourglassHalf,
  FaLock
} from 'react-icons/fa';
import Select from 'react-select';
import { brownSelectStyles as customSelectStyles } from '../utils/selectStyles';
import TableComponent from '../components/TableComponent';
import { generatePointageJournalierPDF } from '../utils/pointageJournalierPDF';
import DocumentsModal from '../components/DocumentsModal';
import KpiCard from '../components/KpiCard';

const PointageJournalierPage = ({ user }) => {
  const [chantiers, setChantiers] = useState([]);
  const [ouvriers, setOuvriers] = useState([]);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dataTable, setDataTable] = useState([]);
  const [dejaPresents, setDejaPresents] = useState([]);
  const [showDejaPresents, setShowDejaPresents] = useState(true);
  const [loading, setLoading] = useState(false);
  const [warningMsg, setWarningMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [showAllOuvriers, setShowAllOuvriers] = useState(false);

  const [showDocsModal, setShowDocsModal] = useState(false);
  const [selectedDocOuvrier, setSelectedDocOuvrier] = useState(null);

  const chantierOptions = useMemo(() => {
    return (chantiers || []).map((c) => ({
      value: c.id,
      label: c.name
    }));
  }, [chantiers]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const ouvrierUrl = showAllOuvriers ? '/ouvriers?nonbloque=1' : '/ouvriers';
        const [resChantiers, resOuvriers] = await Promise.all([
          axios.get('/chantiers'),
          axios.get(ouvrierUrl)
        ]);

        const chantierList = Array.isArray(resChantiers.data) ? resChantiers.data : [];
        const ouvrierList = Array.isArray(resOuvriers.data) ? resOuvriers.data : [];

        setChantiers(chantierList);
        setOuvriers(ouvrierList);

        if (!selectedChantier && chantierList.length > 0) {
          setSelectedChantier({
            value: chantierList[0].id,
            label: chantierList[0].name
          });
        }
      } catch (err) {
        console.error(err);
        setWarningMsg('⚠️ Erreur chargement initial.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllOuvriers]);

  const buildRowsFromOuvriers = (ouvrierList, dejaIds = [], showAll = false) => {
    const chantierId = Number(selectedChantier?.value || 0);

    return (ouvrierList || [])
      .filter((o) => {
        if (o.bloque) return false;
        if (!showAll && Number(o.chantier_id || 0) !== chantierId) return false;
        return true;
      })
      .map((o) => {
        const chantierLabel = showAll
          ? o.Chantier?.name || o.chantier_name || '—'
          : selectedChantier?.label || '';
        return {
          id: o.id,
          cin: o.cin || '',
          name: o.name || '',
          qualification: o.qualification || '',
          matricule_sage: o.matricule_sage || '',
          chantier_actuel: chantierLabel,
          chantier_id: o.chantier_id || null,
          jours: '',
          heures: '',
          bloque: !!o.bloque,
          dejaPresent: dejaIds.includes(Number(o.id)),
          observation: o.observation || ''
        };
      });
  };

  const handleVerifier = async () => {
    setWarningMsg('');
    setSuccessMsg('');

    if (!date || !selectedChantier?.value) {
      setDataTable([]);
      setDejaPresents([]);
      return;
    }

    try {
      setLoading(true);

      // Utilise l'endpoint sans filtre de scope pour détecter les doublons tous chantiers confondus
      const res = await axios.get(`/pointages/all?date=${date}`);
      const dejaPointes = Array.isArray(res.data) ? res.data : [];
      const idsPointes = dejaPointes.map((p) => Number(p.ouvrier?.id)).filter(Boolean);

      const chantierName = selectedChantier?.label || '';
      const noms = showAllOuvriers
        ? dejaPointes.map((p) => p.ouvrier?.name).filter(Boolean)
        : dejaPointes.filter((p) => p.chantier === chantierName).map((p) => p.ouvrier?.name).filter(Boolean);

      setDejaPresents(noms);
      setDataTable(buildRowsFromOuvriers(ouvriers, idsPointes, showAllOuvriers));
    } catch (err) {
      console.error(err);
      setWarningMsg('⚠️ Erreur lors de la vérification.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (date && ouvriers.length > 0 && (selectedChantier?.value || showAllOuvriers)) {
      handleVerifier();
    } else {
      setDataTable([]);
      setDejaPresents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChantier, date, ouvriers, showAllOuvriers]);

  const handleCellChange = (rowId, field, value) => {
    setDataTable((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const handleOpenDocuments = (row) => {
    setSelectedDocOuvrier(row);
    setShowDocsModal(true);
  };

  const sendToChantierTout = async (id) => {
    try {
      await axios.put(`/ouvriers/${id}/envoyer-tout`);
      const ouvrier = ouvriers.find((o) => o.id === id);
      setSuccessMsg(`✅ ${ouvrier?.name || 'Ouvrier'} envoyé vers le chantier TOUT.`);
      const ouvrierUrl = showAllOuvriers ? '/ouvriers?nonbloque=1' : '/ouvriers';
      const res = await axios.get(ouvrierUrl);
      setOuvriers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setWarningMsg('❌ Erreur lors de l\'envoi vers chantier TOUT.');
    }
  };

  const handleValider = async () => {
    setWarningMsg('');
    setSuccessMsg('');

    if (!selectedChantier?.value && !showAllOuvriers) {
      setWarningMsg('❗ Veuillez sélectionner un chantier.');
      return;
    }

    const payload = dataTable
      .filter((row) => !row.dejaPresent && !row.bloque)
      .map((row) => ({
        ouvrier_id: row.id,
        chantier_id: selectedChantier?.value || row.chantier_id,
        date,
        jours: row.jours === '' ? 0 : Number(row.jours),
        heures: row.heures === '' ? 0 : Number(row.heures),
        observation: row.observation || ''
      }))
      .filter((row) => Number(row.jours) > 0 || Number(row.heures) > 0);

    if (!payload.length) {
      setWarningMsg('⚠️ Aucun pointage valide à enregistrer.');
      return;
    }

    const invalid = payload.find((p) => Number(p.jours) < 0 || Number(p.heures) < 0);
    if (invalid) {
      setWarningMsg('❗ Jours et heures doivent être supérieurs ou égaux à 0.');
      return;
    }

    try {
      const res = await axios.post('/pointages', payload);
      const { created = [], doublons = [] } = res.data || {};

      if (created.length > 0 && doublons.length === 0) {
        setSuccessMsg(`✅ ${created.length} pointage(s) enregistré(s) avec succès.`);
      } else if (created.length > 0 && doublons.length > 0) {
        setSuccessMsg(`✅ ${created.length} pointage(s) enregistré(s).`);
        setWarningMsg(`⚠️ ${doublons.length} doublon(s) ignoré(s) : ${doublons.map((d) => d.nom).join(', ')}`);
      } else if (doublons.length > 0) {
        setWarningMsg(`⚠️ Aucun pointage enregistré — doublons détectés : ${doublons.map((d) => d.nom).join(', ')}`);
        await handleVerifier();
        return;
      } else {
        setWarningMsg('⚠️ Aucun pointage valide à enregistrer.');
        return;
      }

      await handleVerifier();
      if (window.confirm('Télécharger le PDF du pointage ?')) {
        generatePointageJournalierPDF({
          chantierName: selectedChantier.label,
          userName: user?.login || user?.name || 'Utilisateur',
          date,
          dataTable
        });
      }
    } catch (err) {
      console.error(err);
      setWarningMsg(err?.response?.data?.message || '❌ Erreur lors de l’enregistrement.');
    }
  };

  const stats = useMemo(() => {
    const total = dataTable.length;
    const deja = dataTable.filter((r) => r.dejaPresent).length;
    const bloques = dataTable.filter((r) => r.bloque).length;
    const restants = total - deja - bloques;

    const totalJours = dataTable.reduce((sum, r) => sum + Number(r.jours || 0), 0);
    const totalHeures = dataTable.reduce((sum, r) => sum + Number(r.heures || 0), 0);

    return {
      total,
      deja,
      bloques,
      restants: restants < 0 ? 0 : restants,
      totalJours: Number(totalJours.toFixed(2)),
      totalHeures: Number(totalHeures.toFixed(2))
    };
  }, [dataTable]);

  return (
    <div className="container mt-4 mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div>
          <h2 className="d-flex align-items-center gap-2 mb-1" style={{ color: '#6d4e36', fontWeight: 800 }}>
            <FaHardHat /> Pointage journalier
          </h2>
          <div className="text-muted">
            Saisie journalière des ouvriers avec vérification, blocage et édition confortable.
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Badge bg="light" text="dark" style={{ border: '1px solid #d8c8b8', padding: '10px 12px' }}>
            👤 {user?.login || user?.name || 'Utilisateur'}
          </Badge>
          {selectedChantier?.label ? (
            <Badge bg="dark" style={{ padding: '10px 12px' }}>
              🏗️ {selectedChantier.label}
            </Badge>
          ) : null}
        </div>
      </div>

      {warningMsg ? (
        <Alert variant="warning" className="mt-3" style={{ border: '1px solid #6d4e36' }}>
          {warningMsg}
        </Alert>
      ) : null}

      {successMsg ? (
        <Alert variant="success" className="mt-3" style={{ border: '1px solid #6d4e36' }}>
          {successMsg}
        </Alert>
      ) : null}

      <Card className="mb-3" style={{ borderRadius: '16px', border: '1px solid rgba(109,78,54,0.18)', boxShadow: '0 8px 24px rgba(109,78,54,0.08)', background: '#fffdf8' }}>
        <Card.Body>
          <Row className="g-3">
            <Col md={5}>
              <Form.Label className="fw-bold">🏗️ Chantier</Form.Label>
              <Select
                isClearable
                isSearchable
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder="Choisir un chantier..."
                options={chantierOptions}
                value={selectedChantier}
                onChange={(val) => setSelectedChantier(val || null)}
              />
            </Col>

            <Col md={3}>
              <Form.Label className="fw-bold">📅 Date</Form.Label>
              <Form.Control
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Col>

            <Col md={4} className="d-flex align-items-end">
              <div className="d-flex gap-2 flex-wrap w-100">
                <Button
                  variant="outline-primary"
                  onClick={handleVerifier}
                  disabled={loading || (!selectedChantier?.value && !showAllOuvriers)}
                >
                  <FaSearch className="me-2" />
                  Vérifier
                </Button>

                <Button
                  variant="primary"
                  onClick={handleValider}
                  disabled={loading || (!selectedChantier?.value && !showAllOuvriers) || !date}
                >
                  <FaSave className="me-2" />
                  Valider
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={() =>
                    generatePointageJournalierPDF({
                      chantierName: selectedChantier?.label || 'Tous les chantiers',
                      userName: user?.login || user?.name || 'Utilisateur',
                      date,
                      dataTable
                    })
                  }
                  disabled={!selectedChantier?.value && !showAllOuvriers}
                >
                  <FaPrint className="me-2" />
                  PDF
                </Button>
              </div>
            </Col>

            <Col md={12}>
              <Form.Check
                type="switch"
                id="switch-all-ouvriers"
                label={
                  <span>
                    <FaUser className="me-1" />
                    Afficher tous les ouvriers (tous chantiers)
                  </span>
                }
                checked={showAllOuvriers}
                onChange={(e) => setShowAllOuvriers(e.target.checked)}
                style={{ color: '#6d4e36', fontWeight: 600 }}
              />
            </Col>

            <Col md={12}>
              {loading ? (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  Chargement...
                </div>
              ) : (
                <div className="text-muted small">
                  💡 Les ouvriers bloqués ou déjà pointés restent visibles mais ne sont pas modifiables.
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={2}>
          <KpiCard title="Total" value={stats.total} sub="Ouvriers affichés" color="#6d4e36" bg="#f8f2ea" icon={FaUser} />
        </Col>

        <Col md={2}>
          <KpiCard title="Déjà pointés" value={stats.deja} sub="Traités aujourd’hui" color="#198754" bg="#eefbf3" icon={FaCheckCircle} />
        </Col>

        <Col md={2}>
          <KpiCard title="Restants" value={stats.restants} sub="À saisir" color="#d17b00" bg="#fff7ea" icon={FaHourglassHalf} />
        </Col>

        <Col md={2}>
          <KpiCard title="Bloqués" value={stats.bloques} sub="Non modifiables" color="#dc3545" bg="#fff1f1" icon={FaLock} alert={stats.bloques > 0} />
        </Col>

        <Col md={2}>
          <KpiCard title="Total jours" value={stats.totalJours} sub="Saisie provisoire" color="#0d6efd" bg="#eef7ff" icon={FaCalendarDay} />
        </Col>

        <Col md={2}>
          <KpiCard title="Total heures" value={stats.totalHeures} sub="Saisie provisoire" color="#3751d1" bg="#eef3ff" icon={FaClock} />
        </Col>
      </Row>

      {(selectedChantier?.value || showAllOuvriers) && (
        <Card className="mb-3" style={{ borderRadius: '16px', border: '1px solid rgba(109,78,54,0.18)', boxShadow: '0 8px 24px rgba(109,78,54,0.08)' }}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <strong className="d-flex align-items-center gap-2">
                  <FaListUl />
                  Déjà pointés
                </strong>
                <div className="text-muted small">
                  {showAllOuvriers
                    ? <>Ouvriers déjà pointés (tous chantiers) à la date <strong>{date}</strong></>
                    : <>Ouvriers déjà pointés pour <strong>{selectedChantier?.label}</strong> à la date <strong>{date}</strong></>
                  }
                </div>
              </div>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowDejaPresents((v) => !v)}
              >
                {showDejaPresents ? <FaEyeSlash className="me-2" /> : <FaEye className="me-2" />}
                {showDejaPresents ? 'Masquer' : 'Afficher'}
              </Button>
            </div>

            <Collapse in={showDejaPresents}>
              <div className="mt-3">
                {dejaPresents.length === 0 ? (
                  <div className="text-success d-flex align-items-center gap-2">
                    <FaCheckCircle /> Aucun ouvrier déjà pointé.
                  </div>
                ) : (
                  <Row className="g-2">
                    {dejaPresents.map((name, idx) => (
                      <Col md={3} key={idx}>
                        <Card style={{ background: '#f4f4f4', border: '1px solid #e1e1e1' }}>
                          <Card.Body className="py-2">
                            <strong>{name}</strong>
                            <div className="small text-muted">Déjà pointé</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      )}

      {!selectedChantier?.value && !showAllOuvriers ? (
        <Alert variant="warning" style={{ border: '1px solid #6d4e36' }}>
          ✅ Sélectionne un <strong>chantier</strong> ou active <strong>«&nbsp;Afficher tous les ouvriers&nbsp;»</strong> pour commencer la saisie.
        </Alert>
      ) : (
        <TableComponent
          data={dataTable}
          onCellChange={handleCellChange}
          onOpenDocuments={handleOpenDocuments}
          onSendToTout={sendToChantierTout}
          title="Liste des ouvriers du chantier"
          showExport
        />
      )}

      <DocumentsModal
        show={showDocsModal}
        onHide={() => setShowDocsModal(false)}
        module="ouvrier"
        moduleId={selectedDocOuvrier?.id}
        itemLabel={
          selectedDocOuvrier
            ? `${selectedDocOuvrier.name || ''}${selectedDocOuvrier.cin ? ` - ${selectedDocOuvrier.cin}` : ''}`
            : ''
        }
        title="Documents de l'ouvrier"
        canDelete={['admin', 'responsable', 'pointeur'].includes(user?.role)}
      />
    </div>
  );
};

export default PointageJournalierPage;